import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DateTimePicker } from '@/components/ui/datetime-picker';
import { Assignment, Employee, Project, DailyHourOverride } from './types';
import { DateRange } from "react-day-picker";
import { HourOverridesTable } from './HourOverridesTable';
import { addDays, format } from 'date-fns';
import { useTranslation } from '@/i18n/useTranslation';
import { Slider } from '@/components/ui/slider';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';

interface AssignmentFormProps {
  selectedEmployee: Employee | null;
  selectedAssignment: Assignment | null;
  selectedProject: string;
  hourlyRate: string;
  dailyHours: string;
  isBillable: boolean; // New prop
  projects: Project[];
  newAssignmentDates: DateRange;
  overrides: DailyHourOverride[];
  onProjectChange: (value: string) => void;
  onHourlyRateChange: (value: string) => void;
  onDailyHoursChange: (value: string) => void;
  onBillableChange: (value: boolean) => void; // New handler
  onDatesChange: (type: 'from' | 'to', value: string) => void;
  onAddOverride: () => void;
  onUpdateOverride: (id: number, field: keyof DailyHourOverride | string, value: string) => Promise<void>;
  onDeleteOverride: (id: number) => Promise<void>;
}

export function AssignmentForm({
  selectedEmployee,
  selectedAssignment,
  selectedProject,
  hourlyRate,
  dailyHours,
  isBillable,
  projects,
  newAssignmentDates,
  overrides,
  onProjectChange,
  onHourlyRateChange,
  onDailyHoursChange,
  onBillableChange,
  onDatesChange,
  onAddOverride,
  onUpdateOverride,
  onDeleteOverride,
}: AssignmentFormProps) {
  const [showRateField, setShowRateField] = useState(false);
  const { t } = useTranslation();
  const dailyHoursValue = parseInt(dailyHours) || 8;

  // Get the current assignment dates for date constraints
  const assignmentStartDate = selectedAssignment 
    ? selectedAssignment.start_date
    : newAssignmentDates.from;
  
  const assignmentEndDate = selectedAssignment 
    ? selectedAssignment.end_date
    : newAssignmentDates.to;

  // Debug log for assignment
  useEffect(() => {
    if (selectedAssignment) {
      console.log("Selected assignment in form:", selectedAssignment);
      console.log("Assignment daily hours:", selectedAssignment.daily_hours);
    }
  }, [selectedAssignment]);

  useEffect(() => {
    const projectData = projects.find(p => p.id.toString() === selectedProject);
    setShowRateField(projectData?.type === 'time_materials');
  }, [selectedProject, projects]);

  return (
    <div className="space-y-4">
      <div>
        <h4 className="text-sm font-medium mb-2">{t('Employee')}</h4>
        <p className="text-sm">{selectedEmployee?.name}</p>
      </div>
      <div>
        <h4 className="text-sm font-medium mb-2">{t('Project')}</h4>
        <Select value={selectedProject} onValueChange={onProjectChange}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder={t('Select a project')} />
          </SelectTrigger>
          <SelectContent>
            {projects?.map((project) => (
              <SelectItem key={project.id} value={project.id.toString()}>
                {project.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <h4 className="text-sm font-medium mb-2">{t('Start Date')}</h4>
        <div className="flex items-center gap-2">
          <Input
            type="date"
            value={selectedAssignment?.start_date 
              ? format(new Date(selectedAssignment.start_date), 'yyyy-MM-dd')
              : newAssignmentDates.from 
                ? format(new Date(newAssignmentDates.from), 'yyyy-MM-dd') 
                : ''
            }
            onChange={(e) => {
              const date = e.target.value ? new Date(e.target.value) : null;
              if (date) {
                onDatesChange('from', date.toISOString());
              }
            }}
          />
        </div>
      </div>
      <div>
        <h4 className="text-sm font-medium mb-2">{t('End Date')}</h4>
        <div className="flex items-center gap-2">
          <Input
            type="date"
            value={selectedAssignment?.end_date 
              ? format(new Date(selectedAssignment.end_date), 'yyyy-MM-dd')
              : newAssignmentDates.to 
                ? format(new Date(newAssignmentDates.to), 'yyyy-MM-dd') 
                : ''
            }
            onChange={(e) => {
              const date = e.target.value ? new Date(e.target.value) : null;
              if (date) {
                onDatesChange('to', date.toISOString());
              }
            }}
          />
        </div>
      </div>
      {showRateField && (
        <div>
          <h4 className="text-sm font-medium mb-2">{t('Hourly Rate (USD)')}</h4>
          <Input
            type="number"
            value={hourlyRate}
            onChange={(e) => onHourlyRateChange(e.target.value)}
            placeholder={t('Enter hourly rate')}
          />
        </div>
      )}
      
      {/* Daily Hours slider */}
      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <Label htmlFor="dailyHours">{t('Daily Hours')}</Label>
          <span className="text-sm font-medium">{dailyHoursValue} hours</span>
        </div>
        <Slider
          id="dailyHours"
          value={[dailyHoursValue]}
          min={0}
          max={16}
          step={1}
          className="w-full"
          onValueChange={(value) => onDailyHoursChange(value[0].toString())}
        />
      </div>
      
      {/* Billable toggle */}
      <div className="flex items-center space-x-2">
        <Switch 
          id="isBillable" 
          checked={isBillable} 
          onCheckedChange={onBillableChange}
        />
        <Label htmlFor="isBillable">{t('Billable')}</Label>
      </div>
      
      {/* Daily hours exceptions table - always show this section */}
      <div className="pt-2">
        <HourOverridesTable
          overrides={overrides}
          onAdd={onAddOverride}
          onUpdate={onUpdateOverride}
          onDelete={onDeleteOverride}
          assignmentStartDate={assignmentStartDate || undefined}
          assignmentEndDate={assignmentEndDate || undefined}
        />
      </div>
    </div>
  );
}
